import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Bell } from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card, CardHeader } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { SkeletonRows } from '@/components/ui/Skeleton';
import { EmptyState } from '@/components/states/EmptyState';
import { ErrorState } from '@/components/states/ErrorState';
import { AlertDeliveryTable } from '@/features/crashes/AlertDeliveryTable';
import { useAlerts } from '@/features/shared/queries';
import { cn } from '@/lib/cn';
import type { AlertStatus } from '@/types/api';

const FILTERS: { value: AlertStatus | 'all'; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'failed', label: 'Failed' },
  { value: 'pending', label: 'Sending' },
  { value: 'sent', label: 'Delivered' },
];

/**
 * The delivery log, grouped by the crash that produced it. Alerts only mean
 * something in the context of their event — a lone failed SMS row does not tell you
 * whether anyone was reached.
 */
export function AlertsPage() {
  const alerts = useAlerts(200);
  const [filter, setFilter] = useState<AlertStatus | 'all'>('all');

  const rows = (alerts.data ?? []).filter(
    (alert) => filter === 'all' || alert.status === filter
  );

  const grouped = rows.reduce<Map<number, typeof rows>>((map, alert) => {
    const existing = map.get(alert.crash_event_id) ?? [];
    existing.push(alert);
    map.set(alert.crash_event_id, existing);
    return map;
  }, new Map());

  // Failures first — they are the rows that need somebody to act.
  const groups = [...grouped.entries()].sort(([, a], [, b]) => {
    const aFailed = a.some((alert) => alert.status === 'failed') ? 1 : 0;
    const bFailed = b.some((alert) => alert.status === 'failed') ? 1 : 0;
    if (aFailed !== bFailed) return bFailed - aFailed;
    return 0;
  });

  const failedTotal = (alerts.data ?? []).filter((alert) => alert.status === 'failed').length;

  return (
    <>
      <PageHeader
        title="Alerts"
        description="Whether each crash notification actually reached its destination."
      />

      <div className="mb-4 flex flex-wrap gap-2">
        {FILTERS.map((option) => (
          <Button
            key={option.value}
            size="sm"
            variant={filter === option.value ? 'primary' : 'secondary'}
            onClick={() => setFilter(option.value)}
          >
            {option.label}
            {option.value === 'failed' && failedTotal > 0 && (
              <span
                className={cn(
                  'ml-1 rounded-full px-1.5 text-[11px]',
                  filter === 'failed' ? 'bg-white/20' : 'bg-bad-soft text-bad'
                )}
              >
                {failedTotal}
              </span>
            )}
          </Button>
        ))}
      </div>

      {alerts.isPending ? (
        <Card>
          <SkeletonRows rows={5} />
        </Card>
      ) : alerts.isError ? (
        <ErrorState error={alerts.error} onRetry={alerts.refetch} />
      ) : groups.length === 0 ? (
        <Card>
          <EmptyState
            icon={Bell}
            title={filter === 'all' ? 'No alerts yet' : `No ${filter} alerts`}
            description={
              filter === 'all'
                ? 'Alerts appear here when a crash is detected and notifications are dispatched.'
                : 'Try a different filter.'
            }
          />
        </Card>
      ) : (
        <div className="space-y-4">
          {groups.map(([crashEventId, group]) => (
            <Card key={crashEventId}>
              <CardHeader
                title={`Crash event #${crashEventId}`}
                description={`${group.length} notification${group.length === 1 ? '' : 's'}`}
                action={
                  <Link
                    to={`/crashes/${crashEventId}`}
                    className="text-[13px] font-medium text-accent hover:text-accent-hover"
                  >
                    View event
                  </Link>
                }
              />
              <AlertDeliveryTable alerts={group} />
            </Card>
          ))}
        </div>
      )}
    </>
  );
}
